import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ElasticVehiclesResponse } from '@//app/api/search/vehicles';
import { useApiBaseUrl } from '@//providers/MarketSettingsProvider';
import { httpClientGetJson } from '../../httpClient';
import { createSearchParams, type OverrideParams } from '../../utils';

export const overrideVehicleSearchParams: OverrideParams = {
  hits: 24,
};

interface TResolveTransformedResultOptions {
  queryClient: QueryClient;
  result: ElasticVehiclesResponse;
  queryString: string;
}

// The API may append duplicate facet values when a query (q) partially matches a facet
// but isn't fully consumed (e.g., "alfa romeo 156" adds manufacturer=Alfa+Romeo each request).
// Deduplicating prevents the client from seeing a perpetual mismatch and looping infinitely.
const deduplicateParams = (params: URLSearchParams): URLSearchParams => {
  const deduplicated = new URLSearchParams();
  const seen = new Map<string, Set<string>>();
  params.forEach((value, key) => {
    const values = seen.get(key) ?? new Set();
    if (!values.has(value)) {
      values.add(value);
      seen.set(key, values);
      deduplicated.append(key, value);
    }
  });
  return deduplicated;
};

const resolveTransformedResult = ({
  queryClient,
  result,
  queryString,
}: TResolveTransformedResultOptions): ElasticVehiclesResponse => {
  // Create a clone to avoid mutating the original result object
  const clonedResult = structuredClone(result);
  const { query } = clonedResult;

  // Build and sort the URL search parameters for consistency
  const params = deduplicateParams(new URLSearchParams(query.parameters));
  // Don't use the backend's queryString as it may contain escaped characters
  // Keep the original 'q' parameter from query.parameters instead
  params.sort();
  const sortedParamsString = params.toString();
  const transformedQueryStringKey = `?${sortedParamsString}`;

  // If there’s a mismatch, update the query and cache the transformed result
  if (queryString !== transformedQueryStringKey) {
    const updatedResult = structuredClone(clonedResult);
    updatedResult.query.parameters = sortedParamsString;
    updatedResult.query.queryString = null;
    updatedResult.query.transformedParameters = sortedParamsString;
    queryClient.setQueryData(['searchVehicles', transformedQueryStringKey], updatedResult);
    return updatedResult;
  }

  return clonedResult;
};

const useVehicles = (params: URLSearchParams) => {
  const apiBaseUrl = useApiBaseUrl();
  const queryClient = useQueryClient();
  const { queryString } = createSearchParams(params, overrideVehicleSearchParams);

  const query = useQuery<ElasticVehiclesResponse>({
    queryKey: ['vehicles', queryString],
    queryFn: async () => {
      const result = await httpClientGetJson<ElasticVehiclesResponse>(
        `${apiBaseUrl}/api/search/vehicles${queryString}`,
      );
      return resolveTransformedResult({ queryClient, result, queryString });
    },
    placeholderData: (t) => t,
  });

  return query;
};

export default useVehicles;
